import { SectionMessage } from '@/components/shared/SectionMessage';
import { sectionFailure } from '@/components/shared/section-messages';
import { ClaimsSkeleton } from './ClaimsSkeleton';

export type ClaimsSectionState = 'no-search' | 'loading' | 'error' | 'empty' | 'ready';

const DEGRADED_MESSAGE =
  'Claims are temporarily unavailable. Policy information above is unaffected — please try again in a few minutes.';
const EMPTY_MESSAGE = 'No claims were found for this policy.';

interface ClaimsStateInput {
  readonly hasSearch: boolean;
  readonly isPending: boolean;
  readonly isIdle: boolean;
  readonly isError: boolean;
  readonly claimCount: number;
}

/** Which of the section's states applies, decided in one place instead of a chain of early returns. */
export function claimsSectionState({
  hasSearch,
  isPending,
  isIdle,
  isError,
  claimCount,
}: ClaimsStateInput): ClaimsSectionState {
  if (!hasSearch) {
    return 'no-search';
  }
  if (isError) {
    return 'error';
  }
  if (isPending && !isIdle) {
    return 'loading';
  }

  return claimCount === 0 ? 'empty' : 'ready';
}

/**
 * The section's loading, empty and degraded states (US-011). It never renders blank.
 *
 * `errorStatus` is what separates a Claims API that is actually down from the two answers this section
 * used to report as an outage as well: the search matched no policy, and the failed-attempt budget is
 * spent. Both are about the search, not about Claims, so both say what the section above says.
 */
export function ClaimsPlaceholder({
  state,
  error,
  errorStatus,
}: {
  readonly state: Exclude<ClaimsSectionState, 'ready'>;
  readonly error?: unknown;
  readonly errorStatus?: number | undefined;
}): React.JSX.Element | null {
  if (state === 'no-search') {
    return null;
  }

  if (state === 'loading') {
    return <ClaimsSkeleton />;
  }

  if (state === 'empty') {
    return <SectionMessage title="Claims" tone="empty" message={EMPTY_MESSAGE} />;
  }

  const failure = sectionFailure(error, errorStatus, DEGRADED_MESSAGE);
  if (failure === null) {
    // The backend could not be reached; ServiceUnavailableBanner says so once for the page (PP-028).
    return null;
  }

  return <SectionMessage title="Claims" tone={failure.tone} message={failure.message} />;
}
